/**
 * First-run demo seed for the localStorage mock.
 *
 * Gives a brand-new local user a populated family so every module shows real
 * content immediately. Only applied when the namespace is empty (see the mock
 * adapter's `seed` hook). Never runs against the real API.
 */
import type { MockDb } from '@solandia/core';
import { toIsoDate } from '@solandia/core';

export function makeSeed(currentUser: { id: string; displayName: string }) {
  return (db: MockDb): void => {
    const now = new Date();
    const iso = now.toISOString();

    const familyId = 'seed-family-1';
    const otherParentId = 'seed-parent-2';
    const emaId = 'seed-child-ema';
    const jakubId = 'seed-child-jakub';

    db.families.push({
      id: familyId,
      name: 'Naša rodina',
      mode: 'family',
      modeChangedAt: null,
      createdAt: iso,
    });

    db.memberships.push({
      id: 'seed-membership-me',
      familyId,
      userId: currentUser.id,
      role: 'parent',
      permissions: [],
      invitedById: null,
      status: 'active',
      createdAt: iso,
    });
    db.memberships.push({
      id: 'seed-membership-other',
      familyId,
      userId: otherParentId,
      role: 'parent',
      permissions: [],
      invitedById: 'seed-membership-me',
      status: 'active',
      createdAt: iso,
    });
    db.memberships.push({
      id: 'seed-membership-grandma',
      familyId,
      userId: 'seed-grandma',
      role: 'trusted',
      permissions: ['calendar', 'contacts'],
      invitedById: 'seed-membership-me',
      status: 'invited',
      createdAt: iso,
    });

    db.children.push({
      id: emaId,
      familyId,
      name: 'Ema',
      avatarEmoji: '🐥',
      birthDate: '2017-04-12',
    });
    db.children.push({
      id: jakubId,
      familyId,
      name: 'Jakub',
      avatarEmoji: '🦊',
      birthDate: '2014-09-03',
    });

    db.contacts.push({
      id: 'seed-contact-1',
      childId: emaId,
      type: 'doctor',
      label: 'MUDr. Nováková (pediater)',
      phone: '+421 900 123 456',
      address: 'Hlavná 12, Bratislava',
      note: 'Ordinačné hodiny: Po–Pia 8:00–13:00',
      createdById: currentUser.id,
      createdAt: iso,
    });
    db.contacts.push({
      id: 'seed-contact-2',
      childId: emaId,
      type: 'school',
      label: 'ZŠ Slnečná',
      phone: '+421 2 555 12 34',
      address: 'Slnečná 4, Bratislava',
      note: null,
      createdById: currentUser.id,
      createdAt: iso,
    });

    db.health.push({
      id: 'seed-health-ema',
      childId: emaId,
      allergies: 'Peľ tráv, orechy',
      medications: null,
      insurance: 'Všeobecná ZP (25)',
      bloodType: '0+',
      conditions: null,
      notes: 'Pri alergickej reakcii podať antihistaminikum.',
      updatedById: currentUser.id,
      updatedAt: iso,
    });

    const inTwoDays = new Date(now.getTime() + 2 * 86_400_000);
    const handover = new Date(now.getTime() + 4 * 86_400_000);
    db.events.push({
      id: 'seed-event-1',
      childId: emaId,
      title: 'Kontrola u pediatra',
      type: 'appointment',
      startAt: new Date(inTwoDays.setUTCHours(9, 0, 0, 0)).toISOString(),
      endAt: new Date(inTwoDays.setUTCHours(9, 30, 0, 0)).toISOString(),
      allDay: false,
      location: 'Ambulancia MUDr. Nováková',
      note: null,
      createdById: currentUser.id,
      createdAt: iso,
    });
    db.events.push({
      id: 'seed-event-2',
      childId: emaId,
      title: 'Odovzdávka — k mame',
      type: 'handover',
      startAt: new Date(handover.setUTCHours(17, 0, 0, 0)).toISOString(),
      endAt: new Date(handover.setUTCHours(17, 0, 0, 0)).toISOString(),
      allDay: false,
      location: 'Pred školou',
      note: null,
      createdById: currentUser.id,
      createdAt: iso,
    });

    db.custody.push({
      id: 'seed-custody-ema',
      childId: emaId,
      pattern: 'week-week',
      startDate: toIsoDate(now),
      notes: 'Striedanie každý piatok poobede.',
    });

    db.documents.push({
      id: 'seed-doc-1',
      childId: emaId,
      name: 'Rodný list — Ema.pdf',
      category: 'Doklady',
      blobPath: `${familyId}/${emaId}/seed-doc-1`,
      mimeType: 'application/pdf',
      sizeBytes: 248_300,
      uploadedById: currentUser.id,
      note: null,
      createdAt: iso,
    });

    db.messages.push({
      id: 'seed-msg-1',
      familyId,
      authorId: otherParentId,
      body: 'Ema má v stredu krúžok do 16:00, vyzdvihnem ju ja.',
      attachments: [],
      createdAt: new Date(now.getTime() - 2 * 3_600_000).toISOString(),
    });
    db.messages.push({
      id: 'seed-msg-2',
      familyId,
      authorId: currentUser.id,
      body: 'Super, ďakujem. Ja zoberiem Jakuba z tréningu.',
      attachments: [],
      createdAt: new Date(now.getTime() - 1 * 3_600_000).toISOString(),
    });

    db.expenses.push({
      id: 'seed-exp-1',
      familyId,
      childId: emaId,
      payerId: currentUser.id,
      amountMinor: 4500,
      currency: 'EUR',
      category: 'Škola',
      description: 'Školské potreby',
      incurredOn: toIsoDate(now),
      splitPct: 50,
      settledAt: null,
      createdAt: iso,
    });
    db.expenses.push({
      id: 'seed-exp-2',
      familyId,
      childId: jakubId,
      payerId: otherParentId,
      amountMinor: 12000,
      currency: 'EUR',
      category: 'Krúžky',
      description: 'Futbalový tréning — kvartál',
      incurredOn: toIsoDate(now),
      splitPct: 50,
      settledAt: null,
      createdAt: iso,
    });

    db.audit.push({
      id: 'seed-audit-1',
      familyId,
      actorId: currentUser.id,
      action: 'create',
      entityType: 'Family',
      entityId: familyId,
      summary: 'Vytvorená rodina Naša rodina',
      createdAt: iso,
    });
  };
}
